import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { BACKGROUNDS_DEF, MARKET_RULES, RARITIES, RARITY_COLOR, bodyTypeOf, featureUnlock, type FeatureFlag, type FeatureId } from '@lastday/shared';
import { useDev, useDevDropRates, useLogout, useMe } from '@/api/hooks';
import { t } from '@/i18n';
import { cx, durationLabel, useCountdown } from '@/lib/format';
import { Portrait, preloadOwnLook } from './Character';
import { Logo } from './Logo';
import { useNewOutfit } from '@/lib/outfitSeen';
import { useTutorialFocus } from '@/lib/tutorial';
import { useCrewChatUnread } from '@/lib/crewChat';
import { NewDot, NewMark, useNewDot, useSeenOnVisit, useUnlocks, useScheduleLockText } from '@/lib/unlocks';
import { PathIcon, P } from './Icon';
import { Sheet } from './Sheet';
import { Bar, Button, HpBar } from './ui';
import { FeedbackSheet } from './FeedbackSheet';
import { ScrapCounter } from './ScrapCounter';
import { EnergyBadge } from './EnergyBadge';
import { Tip } from './Tip';
import { GuidedOverlay } from './GuidedOverlay';
import { UnlockPopup } from './UnlockPopup';
import { WhatsNew } from './WhatsNew';
import { SoundToggle } from './SoundToggle';

/** Krug 5 (K14.10): the phone tab bar, the five screens used most. Workshop has its own tab. */
const NAV = [
  { to: '/camp', key: 'camp', d: P.camp },
  { to: '/map', key: 'map', d: P.map },
  { to: '/survivor', key: 'survivor', d: P.survivor },
  { to: '/inventory', key: 'inventory', d: P.inventory },
  { to: '/workshop', key: 'workshop', d: P.wrench },
] as const;

/** Desktop sidebar, the main part: the tab bar's five and Skills (K14.10). */
const SIDE_NAV = [...NAV, { to: '/skills', key: 'skills', d: P.skills }] as const;

/**
 * Everything else: the sidebar's lower part and the phone's More sheet. `mobile`: only in the More
 * sheet (the sidebar lists it above). Skills comes first on the phone, ahead of Feed and Collection. 10. 10. 2026
 * (owner): the Horde right under Skills (the first of the sidebar's lower part), locked until it opens (level 5).
 */
const MORE: { to: string; label: () => string; d: string; flag?: FeatureFlag; mobile?: boolean; feature?: FeatureId }[] = [
  { to: '/skills', label: () => t('common.skills'), d: P.skills, mobile: true },
  { to: '/horde', label: () => t('common.horde'), d: P.horde, flag: 'horde', feature: 'hordeJoin' },
  // 11. 10. 2026 (owner, docs/thepit.md): The Pit under the Horde, locked until level 10; nothing while its flag is off.
  { to: '/pit', label: () => t('common.pit'), d: P.attack, flag: 'pit', feature: 'pit' },
  { to: '/feed', label: () => t('common.nav.feed'), d: P.feed },
  // 20. 10. 2026 (owner): the Survivors list, next to the Feed.
  { to: '/players', label: () => t('common.players'), d: P.roster },
  { to: '/collection', label: () => t('common.collection'), d: P.book, feature: 'collection' },
  /** Krug 5 (K15.2): the season path. */
  { to: '/season', label: () => t('common.season'), d: P.flag, feature: 'season' },
  { to: '/survivor/appearance', label: () => t('common.appearance'), d: P.hanger, feature: 'appearance' },
  /** 11. 10. 2026 (owner): the Market (level 10 and an account MARKET_RULES.accountDays days old), only while its flag is on. */
  { to: '/market', label: () => t('market.title'), d: P.market, flag: 'market', feature: 'market' },
  { to: '/history', label: () => t('common.history'), d: P.history },
  // 11. 10. 2026 (owner, BACKLOG #33): crews, from level 8 or the first week ("Opens at level 8" until then).
  { to: '/crew', label: () => t('common.clan'), d: P.shield, flag: 'groups', feature: 'crews' },
  { to: '/friends', label: () => t('common.friends'), d: P.users, flag: 'friends' },
  { to: '/notifications', label: () => t('common.notifications'), d: P.bell },
  { to: '/settings', label: () => t('common.settings'), d: P.gear },
  /** 29. 9. 2026: how everything works. */
  { to: '/guide', label: () => t('common.guide'), d: P.help },
];

/**
 * Which main entry a screen lights. On the phone Skills lights Survivor (its button is there) and
 * the report lights Camp; on the desktop Skills has its own entry. Feed and Horde light More.
 */
function isActive(pathname: string, to: string, desktop = false) {
  if (to === '/map') return pathname.startsWith('/map') || pathname.startsWith('/prepare');
  if (to === '/survivor') return pathname.startsWith('/survivor') || (!desktop && pathname.startsWith('/skills'));
  if (to === '/camp') return pathname.startsWith('/camp') || pathname.startsWith('/report');
  return pathname.startsWith(to);
}

/**
 * A locked entry's tooltip (29. 9. 2026): Skills and Appearance at their level, the season path after the
 * first two runs. The entry is there, grey with a lock; the text is only in the tooltip.
 */
function lockTip(id: FeatureId, sched: string | null = null): string {
  // 11. 10. 2026 (owner): the Market also waits for the account's age (MARKET_RULES.accountDays).
  if (id === 'market') return t('market.lockTip', { n: MARKET_RULES.level, d: MARKET_RULES.accountDays });
  // Package 4, part A: on a schedule its level (10. 10. 2026, owner: "Opens at level N", no days).
  if (sched) return sched;
  const lv = featureUnlock(id).level;
  return lv ? t('common.unlocksAtLevel', { n: lv }) : t('common.opensAfterRuns');
}

/**
 * A locked menu entry: grey, a lock, `Opens at level 5` on hover or tap; it opens nothing. `tab` (10. 10. 2026): the
 * phone's tab bar (the icon over the name, the lock on the icon).
 */
function LockedEntry({ id, label, d, className, iconSize, align, tab, fit }: { id: FeatureId; label: string; d: string; className: string; iconSize?: number; align?: 'left' | 'right'; tab?: boolean; fit?: boolean }) {
  const tip = lockTip(id, useScheduleLockText()(id));
  return (
    <Tip
      tip={tip}
      locked
      align={align}
      fit={fit}
      label={`${t('common.lockedAria', { name: label })}. ${tip}`}
      className="flex"
      triggerTestId={`${id}-locked`}
      testId="locked-tip"
      triggerClassName={className}
    >
      {tab ? (
        <>
          <span className="relative">
            <PathIcon d={d} size={iconSize} />
            <PathIcon d={P.lock} size={12} className="absolute -right-2 -top-1" />
          </span>
          {label}
        </>
      ) : (
        <>
          <PathIcon d={d} size={iconSize} />
          {label}
          <PathIcon d={P.lock} size={14} className="ml-auto" />
        </>
      )}
    </Tip>
  );
}

/** Unspent skill points: the badge on Skills (desktop), on the Survivor tab and on the Skills buttons (phone). */
export function SkillBadge({ n, dot }: { n: number; dot?: boolean }) {
  if (n <= 0) return null;
  const label = n === 1 ? t('survivor.skillPoint') : t('survivor.skillPoints', { n });
  return dot ? (
    <span data-testid="skill-badge" aria-label={label} className="mono absolute -right-2.5 -top-1.5 min-w-4 rounded-full bg-text px-1 text-center text-[10px] font-bold leading-4 text-bg">
      {n}
    </span>
  ) : (
    <span data-testid="skill-badge" aria-label={label} className="mono ml-auto rounded-full bg-text px-1.5 text-[11px] font-bold text-bg">
      +{n}
    </span>
  );
}

function Sidebar() {
  const { pathname } = useLocation();
  const [feedback, setFeedback] = useState(false);
  const me = useMe().data;
  const s = me?.survivor;
  const features = me?.features;
  const sideMoreOn = MORE.some((m) => !m.mobile && pathname.startsWith(m.to));
  const newOutfit = useNewOutfit();
  const u = useUnlocks();
  const dot = useNewDot();
  // 23. 10. 2026 (owner): a crewmate's message not read yet: the dot on Crew.
  const chatDot = useCrewChatUnread() > 0;
  return (
    <aside className="sticky top-0 hidden h-dvh w-[248px] shrink-0 flex-col gap-7 border-r border-border bg-chrome px-3.5 py-6 lg:flex">
      {/* The same logo as the landing, with the red sun (1. 10. 2026). */}
      <NavLink to="/camp" aria-label={t('common.brand')} className="block px-2.5 no-underline">
        <Logo kind="lockup" className="h-7 w-[156px]" />
      </NavLink>
      {/* 13. 10. 2026 (owner): the two lists scroll between the logo and the survivor card when the window is too short
          for them (with Crew, The Pit and Market on, the last entries were cut off). */}
      <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto overflow-x-hidden overscroll-contain px-1" data-testid="side-scroll">
        <nav className="flex flex-col gap-1" data-testid="side-nav">
          {SIDE_NAV.map((n) => {
            // A screen with its own entry below (Feed, Appearance, Horde…) lights only that entry.
            const on = !sideMoreOn && isActive(pathname, n.to, true);
            // 10. 10. 2026 (owner): the Workshop locked like Skills until the schedule opens it.
            if (n.key === 'workshop' && !u.open('craft'))
              return <LockedEntry key={n.to} id="craft" fit label={t(`common.nav.${n.key}`)} d={n.d} className="flex h-11 w-full items-center gap-3 rounded-[6px] px-3 text-left text-[15px] font-medium text-muted hover:bg-raised" />;
            if (n.key === 'skills' && !u.open('skills'))
              return <LockedEntry key={n.to} id="skills" fit label={t(`common.nav.${n.key}`)} d={n.d} className="flex h-11 w-full items-center gap-3 rounded-[6px] px-3 text-left text-[15px] font-medium text-muted hover:bg-raised" />;
            return (
              <NavLink key={n.to} to={n.to} data-on={on || undefined} data-guide={`nav-${n.key}`} className={cx('flex h-11 items-center gap-3 rounded-[6px] px-3 text-[15px] no-underline', on ? 'bg-raised font-semibold !text-text' : 'font-medium !text-muted hover:bg-raised hover:!text-text')}>
                <PathIcon d={n.d} />
                {t(`common.nav.${n.key}`)}
                {n.key === 'skills' && <SkillBadge n={s?.unspentSkill ?? 0} />}
                {/* 10. 10. 2026 (owner): a system opened here and not used yet. */}
                {n.key !== 'skills' && <NewDot show={dot(n.to)} className="ml-auto" />}
              </NavLink>
            );
          })}
        </nav>
        <nav className="flex flex-col gap-0.5 border-t border-border pt-4" data-testid="side-more">
          {MORE.filter((n) => !n.mobile && (!n.flag || features?.[n.flag] !== false)).map((n) => {
            const on = pathname.startsWith(n.to);
            if (n.feature && !u.open(n.feature))
              // 13. 10. 2026 (owner): the tip fits the entry; the Market's long one scrolled the menu sideways.
            return <LockedEntry key={n.to} id={n.feature} fit label={n.label()} d={n.d} iconSize={16} className="flex h-9 w-full items-center gap-3 rounded-[6px] px-3 text-left text-[14px] text-muted hover:bg-raised" />;
            return (
              <NavLink key={n.to} to={n.to} data-on={on || undefined} className={cx('flex h-9 items-center gap-3 rounded-[6px] px-3 text-[14px] no-underline', on ? 'bg-raised !text-text' : '!text-muted hover:bg-raised hover:!text-text')}>
                <PathIcon d={n.d} size={16} />
                {n.label()}
                {n.to !== '/survivor/appearance' && <NewDot show={dot(n.to)} chat={n.to === '/crew' && chatDot} className="ml-auto" />}
                {n.to === '/notifications' && !!s?.unreadNotifications && <span className="mono ml-auto rounded-full bg-text px-1.5 text-[11px] font-bold text-bg">{s.unreadNotifications}</span>}
                {n.to === '/survivor/appearance' && (u.isNew('appearance') ? <NewMark show className="ml-auto" /> : newOutfit && <span className="ml-auto size-2 rounded-full bg-text" aria-label={t('appearance.newOutfit')} />)}
              </NavLink>
            );
          })}
          <button type="button" onClick={() => setFeedback(true)} className="flex h-9 items-center gap-3 rounded-[6px] px-3 text-left text-[14px] text-muted hover:bg-raised hover:text-text">
            <PathIcon d={P.message} size={16} />
            {t('common.feedback.menu')}
          </button>
        </nav>
      </div>
      <FeedbackSheet open={feedback} onClose={() => setFeedback(false)} />
      {s && (
        <NavLink to="/survivor" className="flex shrink-0 items-center justify-between gap-2.5 rounded-[6px] border border-border bg-raised p-3 no-underline">
          <Portrait bodyType={bodyTypeOf(s.bodyType, s.name)} outfit={s.outfit} size={128} lazy={false} className="size-10 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="display text-[16px] font-semibold !text-text">{s.name}</span>
            <span className="text-[13px] text-muted">{BACKGROUNDS_DEF[s.background].name}</span>
          </div>
          <span className="mono text-[18px] font-bold text-text">{s.level}</span>
        </NavLink>
      )}
    </aside>
  );
}

/** 10. 10. 2026 (owner): a visit to a system's screen counts as opening it (its dot goes). */
function SeenOnVisit() {
  useSeenOnVisit();
  return null;
}

/** Mobile tab bar: the five main screens and More, on every screen (krug 3, U1). */
function TabBar() {
  const { pathname } = useLocation();
  const [more, setMore] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const me = useMe().data;
  const unread = me?.survivor?.unreadNotifications ?? 0;
  // The badge only once the tree is open (level 5, 29. 9. 2026); the points wait before that.
  const u = useUnlocks();
  const skillPts = u.open('skills') ? (me?.survivor?.unspentSkill ?? 0) : 0;
  const dot = useNewDot();
  // 10. 10. 2026 (owner): More has a dot when one of its entries has (the Horde, the season path, the Collection book…).
  const moreDot = MORE.some((m) => dot(m.to));
  // 23. 10. 2026 (owner): and when a crewmate wrote (Crew is in the More sheet on the phone).
  const chatDot = useCrewChatUnread() > 0;
  const moreOn = !NAV.some((n) => isActive(pathname, n.to)) && MORE.some((m) => pathname.startsWith(m.to));
  const cls = (on: boolean) => cx('relative flex min-h-14 flex-col items-center justify-center gap-1 text-[12px] no-underline', on ? 'font-semibold !text-text' : 'font-medium !text-muted');
  return (
    <>
      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-40 grid grid-cols-6 border-t border-border bg-chrome px-1 pt-1 lg:hidden" data-testid="tab-bar">
        {NAV.map((n) => {
          const on = isActive(pathname, n.to);
          // 10. 10. 2026 (owner): the Workshop tab locked until the schedule opens it (a tap shows when).
          if (n.key === 'workshop' && !u.open('craft'))
            return <LockedEntry key={n.to} id="craft" label={t(`common.nav.${n.key}`)} d={n.d} iconSize={22} tab align="right" className={cx(cls(false), 'w-full')} />;
          return (
            <NavLink key={n.to} to={n.to} className={cls(on)} data-on={on || undefined} data-guide={`nav-${n.key}`}>
              {on && <span className="absolute inset-x-[30%] -top-[5px] h-[2px] rounded-[1px] bg-text" />}
              <span className="relative">
                <PathIcon d={n.d} size={22} />
                {n.key === 'survivor' && <SkillBadge n={skillPts} dot />}
                {!(n.key === 'survivor' && skillPts > 0) && <NewDot show={dot(n.to)} className="absolute -right-1 -top-0.5 size-2.5" />}
              </span>
              {t(`common.nav.${n.key}`)}
            </NavLink>
          );
        })}
        <button type="button" onClick={() => setMore(true)} className={cls(moreOn)} aria-haspopup="dialog" data-on={moreOn || undefined} data-testid="tab-more" data-guide="nav-more">
          {moreOn && <span className="absolute inset-x-[30%] -top-[5px] h-[2px] rounded-[1px] bg-text" />}
          <span className="relative">
            <PathIcon d={P.menu} size={22} />
            {!!unread && <span className="absolute -right-1 -top-0.5 size-2.5 rounded-full bg-text" aria-label={t('common.unread')} />}
            {!unread && <NewDot show={moreDot} chat={chatDot} className="absolute -right-1 -top-0.5 size-2.5" />}
          </span>
          {t('common.more')}
        </button>
      </nav>
      <MoreSheet open={more} onClose={() => setMore(false)} onFeedback={() => setFeedback(true)} />
      <FeedbackSheet open={feedback} onClose={() => setFeedback(false)} />
    </>
  );
}

/** The More sheet (K14.10): Skills first, then Feed, Collection, Appearance, past reports, Horde, Group, Friends, Notifications, Settings, Feedback. */
function MoreSheet({ open, onClose, onFeedback }: { open: boolean; onClose: () => void; onFeedback: () => void }) {
  const navigate = useNavigate();
  const me = useMe().data;
  const features = me?.features;
  const unread = me?.survivor?.unreadNotifications ?? 0;
  const newOutfit = useNewOutfit();
  const u = useUnlocks();
  const dot = useNewDot();
  const chatDot = useCrewChatUnread() > 0;
  return (
    <Sheet open={open} onClose={onClose} title={t('common.more')}>
      <div className="grid grid-cols-2 gap-2 p-4" data-testid="more-sheet">
        {MORE.filter((m) => !m.flag || features?.[m.flag] !== false).map((m, i) => {
          const locked: FeatureId | null = m.to === '/skills' ? (u.open('skills') ? null : 'skills') : m.feature && !u.open(m.feature) ? m.feature : null;
          return locked ? (
            <LockedEntry key={m.to} id={locked} label={m.label()} d={m.d} align={i % 2 ? 'right' : 'left'} className="flex min-h-14 w-full items-center gap-3 rounded-[6px] border border-dashed border-border bg-bg px-3 text-left text-[15px] text-muted" />
          ) : (
          <button
            key={m.to}
            data-guide={m.to === '/skills' ? 'nav-skills' : undefined}
            onClick={() => {
              onClose();
              navigate(m.to);
            }}
            className="flex min-h-14 items-center gap-3 rounded-[6px] border border-border bg-bg px-3 text-left text-[15px] hover:border-line"
          >
            <PathIcon d={m.d} />
            {m.label()}
            {m.to !== '/survivor/appearance' && m.to !== '/skills' && <NewDot show={dot(m.to)} chat={m.to === '/crew' && chatDot} className="ml-auto" />}
            {m.to === '/notifications' && !!unread && <span className="mono ml-auto rounded-full bg-text px-1.5 text-[11px] font-bold text-bg">{unread}</span>}
            {m.to === '/survivor/appearance' && (u.isNew('appearance') ? <NewMark show className="ml-auto" /> : newOutfit && <span className="ml-auto size-2.5 rounded-full bg-text" aria-label={t('appearance.newOutfit')} />)}
            {m.to === '/skills' && <SkillBadge n={me?.survivor?.unspentSkill ?? 0} />}
          </button>
          );
        })}
        <button
          onClick={() => {
            onClose();
            onFeedback();
          }}
          className="flex min-h-14 items-center gap-3 rounded-[6px] border border-dashed border-border bg-bg px-3 text-left text-[15px] hover:border-line"
        >
          <PathIcon d={P.message} />
          {t('common.feedback.menu')}
        </button>
      </div>
    </Sheet>
  );
}

/** Top bar from the Camp design: name, level, XP bar, energy with regen timer, HP bar. */
export function SurvivorBar({ className, right }: { className?: string; right?: ReactNode }) {
  const me = useMe().data;
  const s = me?.survivor;
  const hpFullMs = useCountdown(s?.hpFullAt ?? null);
  if (!s) return null;
  return (
    <header className={cx('sticky top-0 z-30 border-b border-border/90 bg-chrome/80 px-4 py-3 backdrop-blur-[8px]', className)}>
      <div className="mx-auto grid max-w-[1040px] grid-cols-1 items-center gap-x-8 gap-y-2.5 md:grid-cols-2">
        <div className="flex items-center gap-3">
          {/* HUD portrait (8.3: 128, preloaded with the rest of the player's own look). */}
          <NavLink to="/survivor" className="shrink-0 rounded-full" aria-label={s.name}>
            <Portrait bodyType={bodyTypeOf(s.bodyType, s.name)} outfit={s.outfit} size={128} lazy={false} className="size-10 border-line" />
          </NavLink>
          <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
            <div className="flex items-baseline gap-2">
              <span className="display truncate text-[19px] font-semibold leading-none">{s.name}</span>
              <span className="mono shrink-0 whitespace-nowrap text-[13px] font-bold text-text">{s.rank ? t('common.lvRank', { n: s.level, rank: s.rank }) : t('common.lv', { n: s.level })}</span>
              {/* Skill points count once the tree is open (level 5, 29. 9. 2026). */}
              {s.unspentAttr + (s.unlocks.includes('skills') ? s.unspentSkill : 0) > 0 && (
                <NavLink to="/survivor" className="mono rounded-[3px] bg-text px-1.5 text-[11px] font-bold !text-bg no-underline">
                  +{s.unspentAttr + (s.unlocks.includes('skills') ? s.unspentSkill : 0)}
                </NavLink>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Level 20: the bar fills the next Survivor Rank (krug 5, F12). */}
              <Bar value={s.xpToNext ? s.xp : s.rankXp} max={Math.max(1, s.xpToNext || s.rankXpToNext)} className="flex-1" />
              <span className="mono text-[12px] text-muted">
                {s.xpToNext
                  ? `${s.xp.toLocaleString('en-US')}/${s.xpToNext.toLocaleString('en-US')}`
                  : s.rankXpToNext
                    ? `${s.rankXp.toLocaleString('en-US')}/${s.rankXpToNext.toLocaleString('en-US')}`
                    : 'MAX'}
              </span>
            </div>
          </div>
          {/* Krug 5 (K9b): Scrap, the currency, next to the level and the energy. */}
          <ScrapCounter value={s.scrap} />
          {/* Krug 5 (K17.3): `6/10`, and the pace on hover or tap. */}
          <EnergyBadge s={s} />
          {/* Package 4, part L: all sound off or on. */}
          <SoundToggle />
          {right}
        </div>
        <div className="grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-2">
          <span className="label">{t('common.hp')}</span>
          <HpBar hp={s.hp} max={s.hpMax} height={4} />
          <span className="mono text-[12px]">
            {s.hp}
            <span className="text-muted">/{s.hpMax}</span>
            {/* Resting and hurt: when HP is full again (C5). */}
            {s.hpFullAt != null && hpFullMs > 0 && <span className="text-muted"> · {t('common.hpFullIn', { t: durationLabel(hpFullMs) })}</span>}
          </span>
        </div>
      </div>
    </header>
  );
}

/** Current gear drop chances for this survivor (level, luck and bad-luck counter included). */
function DevDropRates() {
  const q = useDevDropRates(true);
  if (!q.data) return <p className="px-4 pb-3 text-[13px] text-muted">{q.isError ? 'Drop chances unavailable.' : 'Loading drop chances…'}</p>;
  const d = q.data;
  const pct = (n: number) => (n === 0 ? '—' : n < 1 ? n.toFixed(2) : n.toFixed(1));
  return (
    <div className="flex flex-col gap-1.5 px-4 pb-4">
      <span className="label">Drop chances · Lv {d.survivor.level} · luck +{Math.round(d.survivor.luck * 100)}% · pity {d.survivor.pity} · Rare+ ×{d.multiplier}</span>
      <div className="overflow-x-auto">
        <table className="mono w-full border-collapse text-[12px]">
          <thead>
            <tr className="text-muted">
              <th className="py-1 pr-2 text-left font-normal">zone</th>
              {RARITIES.map((r) => (
                <th key={r} className="px-1 py-1 text-right font-normal" style={{ color: RARITY_COLOR[r] }}>
                  {r.slice(0, 3).toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.zones.map((z) => (
              <tr key={z.zone} className="border-t border-border">
                <td className="py-1 pr-2">{z.name}</td>
                {RARITIES.map((r) => (
                  <td key={r} className="px-1 py-1 text-right">{pct(z.chances[r])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Equip tutorial after the first report (6.7): points at the guaranteed Uncommon. 6. 10. 2026: it reports its height
 * (`onHeight`, 0 when it is gone), so the page leaves room under its content and the last card of a short list (the
 * inventory after a first run with one item) can still be scrolled above the banner and tapped.
 */
function TutorialBanner({ onHeight }: { onHeight: (h: number) => void }) {
  const me = useMe().data;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const s = me?.survivor;
  const shown = !!s && s.tutorial === 'equip' && !pathname.startsWith('/inventory/') && !pathname.startsWith('/report');
  // While the banner is up its Equip is the screen's one red button (1. 10. 2026).
  useTutorialFocus(shown);
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!shown || !el) {
      onHeight(0);
      return;
    }
    const report = () => onHeight(el.offsetHeight);
    report();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(report);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      onHeight(0);
    };
  }, [shown, onHeight]);
  if (!shown || !s) return null;
  return (
    <div ref={box} data-testid="tutorial-banner" className="fixed inset-x-3 bottom-[84px] z-40 mx-auto flex max-w-[520px] animate-rise items-center gap-3 rounded-[8px] border border-line bg-surface px-3.5 py-3 shadow-pop lg:bottom-6" role="status">
      <span className="size-2.5 shrink-0 rounded-full bg-uncommon animate-pulse-dot" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-display text-[15px] font-semibold uppercase tracking-[0.06em]">{t('common.tutorial.equipTitle')}</span>
        <span className="text-[13px] leading-[18px] text-muted">{t('common.tutorial.equipBody')}</span>
      </span>
      {/* Equip is the main action while the tutorial is on (owner's decision, 1. 10. 2026): the only red one. */}
      <Button size="sm" data-tutorial-cta="" onClick={() => navigate(s.tutorialItemId ? `/inventory/${s.tutorialItemId}` : '/inventory')}>
        {t('common.tutorial.equipCta')}
      </Button>
    </div>
  );
}

function DevPanel() {
  const me = useMe().data;
  const dev = useDev();
  const [open, setOpen] = useState(false);
  const logout = useLogout();
  if (!me?.features.devTools || !me.survivor) return null;
  const act = (path: 'finish' | 'grant' | 'rush' | 'horde', body?: unknown) => dev.mutate({ path, body });
  return (
    <>
      <button onClick={() => setOpen(true)} className="mono fixed bottom-[84px] left-3 z-50 rounded-[4px] border border-dashed border-line bg-bg/90 px-2 py-1 text-[11px] text-muted lg:bottom-3 lg:left-auto lg:right-3" aria-label={t('common.devTools')}>
        DEV ×{me.features.gameSpeed}
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title={t('common.devTools')}>
        <div className="grid grid-cols-2 gap-2 p-4">
          <Button size="sm" variant="ghost" onClick={() => act('finish')}>Finish run now</Button>
          <Button size="sm" variant="ghost" onClick={() => act('grant', { energy: 10 })}>+10 energy</Button>
          <Button size="sm" variant="ghost" onClick={() => act('grant', { heal: true })}>Full heal</Button>
          <Button size="sm" variant="ghost" onClick={() => act('grant', { xp: 2000 })}>+2,000 XP</Button>
          <Button size="sm" variant="ghost" onClick={() => act('grant', { stacks: { scrap: 500, metal: 300, wood: 200, cloth: 200, electronics: 150, chemicals: 100, medicine: 80, fuel: 60, food: 100, ammo: 50, blueprint: 2, old_map: 1, map_fragment: 3, lab_clue: 3, bandage: 5, medkit: 3, repair_kit: 3 } })}>+Resources</Button>
          <Button size="sm" variant="ghost" onClick={() => act('rush')}>Rush builds</Button>
          <Button size="sm" variant="ghost" onClick={() => act('grant', { item: 'rare' })}>Rare item</Button>
          <Button size="sm" variant="ghost" onClick={() => act('grant', { item: 'epic' })}>Epic item</Button>
          <Button size="sm" variant="ghost" onClick={() => act('grant', { item: 'legendary' })}>Legendary</Button>
          <Button size="sm" variant="ghost" onClick={() => act('grant', { item: 'rare', slot: 'pants' })}>Rare pants</Button>
          <Button size="sm" variant="ghost" onClick={() => act('horde')}>Start horde</Button>
          <Button size="sm" variant="danger-outline" className="col-span-2" onClick={() => logout.mutate(undefined, { onSuccess: () => (window.location.href = '/') })}>Sign out</Button>
        </div>
        {open && <DevDropRates />}
        <p className="px-4 pb-4 text-[13px] text-muted">Dev tools are on because the server runs with DEV_TOOLS=1. Set GAME_SPEED to speed up game time.</p>
      </Sheet>
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  // The player's own pictures load up front (8.3); other players' portraits load lazily.
  const own = useMe().data?.survivor;
  preloadOwnLook(own?.bodyType, own?.outfit);
  // 6. 10. 2026: while the first-run banner is up, room under the page for it (phone: above the tab bar at 84 px;
  // desktop: 24 px from the bottom) and 8 px, so nothing stays under it.
  const [bannerH, setBannerH] = useState(0);
  return (
    <div className="flex min-h-dvh bg-bg">
      <Sidebar />
      <main
        className={cx('min-w-0 flex-1', bannerH > 0 ? 'pb-[calc(92px_+_var(--banner-h))] lg:pb-[calc(32px_+_var(--banner-h))]' : 'pb-[76px] lg:pb-0')}
        style={bannerH > 0 ? ({ '--banner-h': `${bannerH}px` } as CSSProperties) : undefined}
      >
        {children}
      </main>
      <TabBar />
      <TutorialBanner onHeight={setBannerH} />
      {/* Package 4, part A: the guided step (new accounts). */}
      <GuidedOverlay />
      {/* 10. 10. 2026 (owner): a system just opened, without dimming; a visit to its screen takes its dot away. */}
      <UnlockPopup />
      {/* 23. 10. 2026 (owner): what changed in the last update, once, on Camp. */}
      <WhatsNew />
      <SeenOnVisit />
      <DevPanel />
    </div>
  );
}
